import { ClipboardList } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Fact } from '@/features/cases/components/shared/Fact'
import { useFormat } from '@/i18n/useFormat'
import type { CaseDetail } from '@/types/domain'

/** The deal terms in one place: dates, payment, incoterms, who answers for the
 *  case, the issuing entity and the note the client actually cares about. */
export function DetailsCard({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const format = useFormat()

  /* ATD once the cargo has moved, ETD while departure is still a plan — the
     label names which fact the date is, instead of a "departure" that could be
     either. ATD/ETD/ETA stay untranslated like the header band: they are the
     trade's own abbreviations in both offices. */
  const departure = record.atd
    ? `ATD ${format.date(record.atd)}`
    : record.etd
      ? `ETD ${format.date(record.etd)}`
      : null
  const dates = [departure, record.eta ? `ETA ${format.date(record.eta)}` : null]
    .filter(Boolean)
    .join(' · ')

  const terms = [record.incoterm, record.movementScope].filter(Boolean).join(' · ')

  const responsible = [
    record.ownerName,
    record.opsOwnerName
      ? t('cases.overview.opsNamed', { name: record.opsOwnerName })
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList size={15} className="text-text-muted" aria-hidden />
          {t('cases.overview.details')}
        </CardTitle>
      </CardHeader>
      <CardBody className="grid grid-cols-1 gap-x-6 gap-y-4 pt-3 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label={t('cases.overview.dates')}>{dates || '—'}</Fact>
        {/* The terms as a NAMED kind rather than the phrase this card used to
            render (FR-32). A credit term shows its day count beside it, because
            "credit" without the number is not the agreement. */}
        <Fact label={t('cases.overview.paymentTerms')}>
          {record.paymentTermsKind
            ? [
                t(`cases.terms.${record.paymentTermsKind}`),
                record.paymentTermsCreditDays === undefined
                  ? undefined
                  : t('cases.overview.creditDays', {
                      count: record.paymentTermsCreditDays,
                    }),
              ]
                .filter(Boolean)
                .join(' · ')
            : '—'}
        </Fact>
        <Fact label={t('cases.overview.paymentMethod')}>
          {record.paymentMethod ? t(`cases.method.${record.paymentMethod}`) : '—'}
        </Fact>
        <Fact label={t('cases.overview.incoterm')}>{terms || '—'}</Fact>
        <Fact label={t('cases.overview.responsible')}>{responsible || '—'}</Fact>
        <Fact label={t('cases.overview.entity')}>{record.entityName ?? '—'}</Fact>
        {/* Whose numbers the declared measures are (FR-12), carried over from
            the quote at conversion. Never a dash: both states are a real answer,
            and "our estimate" is the one Operations has to know BEFORE the truck
            is booked, so it is stated rather than left to the absence of a
            badge. Worded as the fact it reports, not as a checkbox label. */}
        <Fact label={t('cases.overview.packingList')}>
          {record.finalPackingListReceived
            ? t('cases.overview.packingListReceived')
            : t('cases.overview.packingListEstimate')}
        </Fact>
        <Fact label={t('cases.overview.notes')}>{record.notes ?? '—'}</Fact>
      </CardBody>
    </Card>
  )
}
